// redux/Disputes.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../context/API';
import { Dispute, Stats } from '../components/DisputeManagement/types';

// Disputes API endpoints
const DISPUTES_ENDPOINTS = {
  LIST: "/disputes",
  GET: "/disputes/{id}",
  UPDATE_NOTES: "/disputes/{id}/notes",
  SUBMIT: "/disputes/{id}/submit",
  ANALYTICS: "/analytics/disputes",
} as const;

// Helper functions
const getEndpoint = (template: string, id: string | number): string => 
  template.replace("{id}", String(id));

const computeStats = (disputes: Dispute[]): Stats => {
  return disputes.reduce(
    (acc, dispute) => {
      acc.total++;
      const status = dispute.status;
      if (status === "needs_response") acc.needsResponse++;
      else if (status === "under_review") acc.underReview++;
      else if (status === "won") acc.won++;
      else if (status === "lost") acc.lost++;
      else if (status === "warning_needs_response") acc.warningNeedsResponse++;
      return acc;
    },
    {
      total: 0,
      needsResponse: 0,
      underReview: 0,
      won: 0,
      lost: 0,
      warningNeedsResponse: 0,
    }
  );
};

const updateDisputeInArray = (disputes: Dispute[], updatedDispute: Dispute): void => {
  const index = disputes.findIndex((d) => d.id === updatedDispute.id);
  if (index !== -1) {
    disputes[index] = updatedDispute;
  }
};

// Analytics Disputes Data interface
export interface AnalyticsDisputeData {
  totalDisputes: number;
  resolvedDisputes: number;
  pendingDisputes: number;
  disputes: Array<{
    id: string;
    customerName: string;
    title: string;
    description: string;
    status: "Resolved" | "Pending";
    dateSubmitted: string;
    lastUpdated: string;
  }>;
}

// State interface
export interface DisputesState {
  disputes: Dispute[];
  selectedDispute: Dispute | null;
  loading: boolean;
  loadingDetails: boolean;
  updatingNotes: boolean;
  submittingResponse: boolean;
  error: string | null;
  stats: Stats;
  analyticsData: AnalyticsDisputeData | null;
  analyticsLoading: boolean;
}

const initialState: DisputesState = {
  disputes: [],
  selectedDispute: null,
  loading: false,
  loadingDetails: false,
  updatingNotes: false,
  submittingResponse: false,
  error: null,
  stats: {
    total: 0,
    needsResponse: 0,
    underReview: 0,
    won: 0,
    lost: 0,
    warningNeedsResponse: 0,
  },
  analyticsData: null,
  analyticsLoading: false,
};

// Async thunks
export const fetchDisputes = createAsyncThunk<
  Dispute[],
  { merchantId?: string | null; status?: string | null },
  { rejectValue: string }
>(
  'disputes/fetchDisputes',
  async ({ merchantId = null, status = null }, { rejectWithValue }) => {
    try {
      const params: Record<string, string> = {};
      if (merchantId) params.merchantId = merchantId;
      if (status) params.status = status;
      
      const response = await API.get(DISPUTES_ENDPOINTS.LIST, { params });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch disputes"
      );
    }
  }
);

export const fetchDispute = createAsyncThunk<
  Dispute,
  string | number,
  { rejectValue: string }
>(
  'disputes/fetchDispute',
  async (disputeId, { rejectWithValue }) => {
    try {
      const response = await API.get(getEndpoint(DISPUTES_ENDPOINTS.GET, disputeId));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch dispute"
      );
    }
  }
);

export const updateDisputeNotes = createAsyncThunk<
  Dispute,
  { disputeId: string | number; notes: string },
  { rejectValue: string }
>(
  'disputes/updateDisputeNotes',
  async ({ disputeId, notes }, { rejectWithValue }) => {
    try {
      const response = await API.put(getEndpoint(DISPUTES_ENDPOINTS.UPDATE_NOTES, disputeId), { notes });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update notes"
      );
    }
  }
);

export const submitDisputeResponse = createAsyncThunk<
  Dispute,
  { disputeId: string | number; evidence?: Record<string, any> },
  { rejectValue: string }
>(
  'disputes/submitDisputeResponse',
  async ({ disputeId, evidence = {} }, { rejectWithValue }) => {
    try {
      const response = await API.post(getEndpoint(DISPUTES_ENDPOINTS.SUBMIT, disputeId), { evidence });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to submit dispute response"
      );
    }
  }
);

export const fetchAnalyticsDisputes = createAsyncThunk<
  AnalyticsDisputeData,
  void,
  { rejectValue: string }
>(
  'disputes/fetchAnalyticsDisputes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get(DISPUTES_ENDPOINTS.ANALYTICS);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch analytics disputes"
      );
    }
  }
);

const disputesSlice = createSlice({
  name: 'disputes',
  initialState,
  reducers: {
    setSelectedDispute: (state, action: PayloadAction<Dispute>) => {
      state.selectedDispute = action.payload;
    },
    clearSelectedDispute: (state) => {
      state.selectedDispute = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    calculateStats: (state) => {
      state.stats = computeStats(state.disputes);
    },
  },
  extraReducers: (builder) => {
    // Fetch disputes
    builder
      .addCase(fetchDisputes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDisputes.fulfilled, (state, action) => {
        state.loading = false;
        state.disputes = Array.isArray(action.payload) ? action.payload : [];
        state.stats = computeStats(state.disputes);
      })
      .addCase(fetchDisputes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch disputes";
        state.disputes = [];
      });

    // Fetch single dispute
    builder
      .addCase(fetchDispute.pending, (state) => {
        state.loadingDetails = true;
        state.error = null;
      })
      .addCase(fetchDispute.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.selectedDispute = action.payload;
        updateDisputeInArray(state.disputes, action.payload);
      })
      .addCase(fetchDispute.rejected, (state, action) => {
        state.loadingDetails = false;
        state.error = action.payload || "Failed to fetch dispute";
      });

    // Update dispute notes
    builder
      .addCase(updateDisputeNotes.pending, (state) => {
        state.updatingNotes = true;
        state.error = null;
      })
      .addCase(updateDisputeNotes.fulfilled, (state, action) => {
        state.updatingNotes = false;
        state.selectedDispute = action.payload;
        updateDisputeInArray(state.disputes, action.payload);
      })
      .addCase(updateDisputeNotes.rejected, (state, action) => {
        state.updatingNotes = false;
        state.error = action.payload || "Failed to update notes";
      });

    // Submit dispute response
    builder
      .addCase(submitDisputeResponse.pending, (state) => {
        state.submittingResponse = true;
        state.error = null;
      })
      .addCase(submitDisputeResponse.fulfilled, (state, action) => {
        state.submittingResponse = false;
        state.selectedDispute = action.payload;
        updateDisputeInArray(state.disputes, action.payload);
        state.stats = computeStats(state.disputes);
      })
      .addCase(submitDisputeResponse.rejected, (state, action) => {
        state.submittingResponse = false;
        state.error = action.payload || "Failed to submit dispute response";
      });

    // Fetch analytics disputes
    builder
      .addCase(fetchAnalyticsDisputes.pending, (state) => {
        state.analyticsLoading = true;
        state.error = null;
      })
      .addCase(fetchAnalyticsDisputes.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analyticsData = action.payload;
      })
      .addCase(fetchAnalyticsDisputes.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action.payload || "Failed to fetch analytics disputes";
        state.analyticsData = {
          totalDisputes: 0,
          resolvedDisputes: 0,
          pendingDisputes: 0,
          disputes: [],
        };
      });
  },
});

export const {
  setSelectedDispute,
  clearSelectedDispute,
  setError,
  clearError,
  calculateStats,
} = disputesSlice.actions;

export default disputesSlice.reducer;

